import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { suggestions } from "../api";

export interface RecipientChip {
    email: string;
    name?: string;
}

const EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

export function parseRecipients(text: string): RecipientChip[] {
    return text
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => {
            const m = s.match(/^(.*?)\s*<([^>]+)>$/);
            if (m) return { name: m[1].trim() || undefined, email: m[2].trim() };
            return { email: s };
        })
        .filter((c) => EMAIL_RE.test(c.email));
}

export interface RecipientInputProps {
    label: string;
    value: RecipientChip[];
    onChange: (chips: RecipientChip[]) => void;
    placeholder?: string;
    /** Set when the field should auto-focus on mount (e.g. empty To) */
    autoFocus?: boolean;
}

/** Chip-style recipient input with the SnappyMail Suggestions autocomplete. */
export function RecipientInput({ label, value, onChange, placeholder = "name@example.com", autoFocus = false }: RecipientInputProps) {
    const [text, setText] = useState("");
    const [focused, setFocused] = useState(false);
    const [options, setOptions] = useState<RecipientChip[]>([]);
    const [selected, setSelected] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

    const commitText = () => {
        const chips = parseRecipients(text);
        if (chips.length) {
            const seen = new Set(value.map((c) => c.email.toLowerCase()));
            const merged = [...value];
            for (const c of chips) {
                if (!seen.has(c.email.toLowerCase())) {
                    seen.add(c.email.toLowerCase());
                    merged.push(c);
                }
            }
            onChange(merged);
        }
        setText("");
        setOptions([]);
        setSelected(-1);
        inputRef.current?.focus();
    };

    /* fetch suggestions (debounced) */
    useEffect(() => {
        if (!focused || !text.trim()) {
            setOptions([]);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            suggestions(text.trim())
                .then((list) => {
                    setOptions(
                        list
                            .filter(([email]) => EMAIL_RE.test(email))
                            .map(([email, name]) => ({ email, name }))
                    );
                    setSelected(-1);
                })
                .catch(() => setOptions([]));
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [text, focused]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            if (options.length && selected >= 0) {
                const opt = options[selected];
                onChange([...value, opt]);
                setText("");
                setOptions([]);
                setSelected(-1);
            } else {
                commitText();
            }
        } else if (e.key === "Backspace" && !text && value.length) {
            remove(value.length - 1);
        } else if (e.key === "ArrowDown" && options.length) {
            e.preventDefault();
            setSelected((s) => (s + 1) % options.length);
        } else if (e.key === "ArrowUp" && options.length) {
            e.preventDefault();
            setSelected((s) => (s - 1 + options.length) % options.length);
        } else if (e.key === "Escape") {
            setOptions([]);
        } else if (e.key === "Tab" && text.trim()) {
            commitText();
        }
    };

    return (
        <div className="flex items-start gap-2 border-b border-white/10 py-2">
            <span className="w-16 shrink-0 pt-2 text-[11px] font-black uppercase tracking-[0.15em] text-zinc-500">
                {label}
            </span>
            <div className="relative min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                    {value.map((chip, i) => (
                        <span
                            key={`${chip.email}-${i}`}
                            className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-orange-500/25 bg-orange-500/10 py-1 pl-2.5 pr-1.5 text-xs font-semibold text-orange-300"
                        >
                            <span className="truncate">
                                {chip.name ? `${chip.name} <${chip.email}>` : chip.email}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="cursor-pointer rounded p-0.5 text-orange-400/70 transition-colors hover:bg-orange-500/20 hover:text-orange-300"
                                title="Remove"
                            >
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                    <input
                        ref={inputRef}
                        type="text"
                        value={text}
                        autoFocus={autoFocus}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={onKeyDown}
                        onFocus={() => setFocused(true)}
                        onBlur={() => {
                            /* commit pending text on blur (but allow suggestion clicks) */
                            setTimeout(() => {
                                setFocused(false);
                                if (text.trim()) commitText();
                            }, 150);
                        }}
                        placeholder={value.length ? "" : placeholder}
                        className="min-w-24 flex-1 bg-transparent py-1 text-sm font-medium text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                    />
                </div>

                {/* suggestions */}
                {focused && options.length > 0 && (
                    <div className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border border-white/[0.15] bg-zinc-950/95 p-1 shadow-[0_25px_60px_rgba(0,0,0,0.95)] backdrop-blur-2xl">
                        {options.map((opt, i) => (
                            <button
                                key={opt.email}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    onChange([...value, opt]);
                                    setText("");
                                    setOptions([]);
                                    setSelected(-1);
                                    inputRef.current?.focus();
                                }}
                                className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-xs transition-colors ${
                                    i === selected ? "bg-orange-500/15 text-orange-300" : "text-zinc-300 hover:bg-white/10 hover:text-white"
                                }`}
                            >
                                <span className="truncate font-semibold">{opt.name || opt.email}</span>
                                {opt.name && <span className="truncate text-zinc-500">{opt.email}</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
